<template>
  <transition id="loader" name="fade">
    <div v-if="barLoader" id="bar-loading-wrapper">
      <div class="loading-prog">
        <img class="loader-img" src="#" />
        <h1></h1>
        <div id="loading-filter" :style="`left: ${loaderProgress}%;`"></div>
      </div>
      <div id="loading-bar">
        <div
          id="loading-bar-antiprog"
          :style="`left: ${100 - loaderProgress}%;`"
        ></div>
        <div
          id="loading-bar-prog"
          :style="`right: ${100 - loaderProgress}%;`"
        ></div>
      </div>
    </div>
    <div v-else id="spinner-loading-wrapper">
      <div class="profile-main-loader">
        <div class="loader">
          <svg class="circular-loader" viewBox="25 25 50 50">
            <circle
              class="loader-path"
              cx="50"
              cy="50"
              r="20"
              fill="none"
              stroke="$spin_color"
              stroke-width="2"
            />
          </svg>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { preloadData } from "@/data/preloadData";
export default {
  name: "SiteLoader",
  data() {
    return {
      loaderProgress: 0,
      loaderProgressQueue: 0,
      preloadImageUrls: preloadData().images,
      preloadFontsArray: preloadData().fonts,
      preloadMinimumFinished: false,
      minLoadMilli: 2000,
      digestLoaderInterval: null,
    };
  },
  props: {
    barLoader: {
      type: Boolean,
      required: false,
    },
  },
  watch: {
    loaderProgress: function (newVal, oldVal) {
      if (newVal > 99) {
        this.$emit("setLoaderFinished", true);
        document.documentElement.style.overflow = "auto";
      }
    },
  },
  computed: {
    ...mapState(["global"]),
  },
  mounted() {
    //add items to preload in src/data/preloadData.js
    this.preloadEverything();
    document.documentElement.style.overflow = "hidden";
  },
  beforeDestroy() {
    document.documentElement.style.overflow = "auto";
  },
  methods: {
    preloadEverything() {
      setTimeout(() => {
        this.preloadMinimumFinished = true;
      }, 800);

      const itemIncrementPercentage =
        (1 / (this.preloadFontsArray.length + this.preloadImageUrls.length)) *
        100;

      //loader progress starts at item increment percentage so that bar is full when loading last item instead of 100-itemincrement
      this.loaderProgress += itemIncrementPercentage;
      this.preloadFonts(this.preloadFontsArray, itemIncrementPercentage, () => {
        this.setLoadedItem({
          loadedProperty: "fonts",
          isLoaded: true,
        });
      });

      this.preloadImages(this.preloadImageUrls, itemIncrementPercentage, () => {
        this.setLoadedItem({
          loadedProperty: "images",
          isLoaded: true,
        });
      });
      this.digestLoaderInterval = setInterval(() => {
        this.digestLoaderProgress();
      }, 50);
    },
    digestLoaderProgress(itemIncrementPercentage) {
      const incrementMilli = this.minLoadMilli / 100;
      let currentTimeout = 0;
      let that = this;

      if (
        this.loaderProgress >= 100 ||
        (this.preloadMinimumFinished & this.global.loadedItems.images &&
          this.global.loadedItems.fonts)
      ) {
        clearInterval(this.digestLoaderInterval);
        return;
      }

      const amountToDigest = this.loaderProgressQueue;
      for (let i = 0; i < amountToDigest - 1; i++) {
        if (this.loadersProgress >= 100) break;

        if (100 - this.loaderProgress < itemIncrementPercentage) {
          this.loaderProgress = 100;
          break;
        } else {
          setTimeout(function () {
            that.loaderProgress += 1;
          }, currentTimeout + incrementMilli);
          currentTimeout += incrementMilli;
        }
      }
      this.loaderProgressQueue -= amountToDigest;
    },
    incrementLoaderProgress(itemIncrementPercentage) {
      this.loaderProgressQueue += itemIncrementPercentage;
    },
    preloadFonts(fonts, itemIncrementPercentage, allFontsLoadedCallback) {
      if (fonts.length < 1) {
        this.setLoadedItem({
          loadedProperty: "fonts",
          isLoaded: true,
        });
        return;
      }
      let loadedCounter = 0;
      fonts.forEach((font) => {
        font
          .load()
          .then((loadedFont) => {
            loadedCounter++;
            this.incrementLoaderProgress(itemIncrementPercentage);
            document.fonts.add(loadedFont);
            if (loadedCounter === fonts.length) {
              allFontsLoadedCallback();
            }
          })
          .catch(() => {
            loadedCounter++;
            this.incrementLoaderProgress(itemIncrementPercentage);
            if (loadedCounter === fonts.length) {
              allFontsLoadedCallback();
            }
          });
      });
    },
    preloadImages(urlArray, itemIncrementPercentage, allImagesLoadedCallback) {
      if (urlArray.length < 1) {
        this.setLoadedItem({
          loadedProperty: "images",
          isLoaded: true,
        });
        return;
      }
      let loadedCounter = 0;
      urlArray.forEach((url) => {
        preloadImage(url, () => {
          loadedCounter++;
          this.incrementLoaderProgress(itemIncrementPercentage);
          if (loadedCounter === urlArray.length) {
            allImagesLoadedCallback();
          }
        });
      });
      function preloadImage(url, imageLoadedCallback) {
        let img = new Image();
        img.onload = imageLoadedCallback;
        img.onerror = imageLoadedCallback;
        img.src = url;
      }
    },
    ...mapActions(["setLoadedItem"]),
  },
};
</script>
<style lang="scss" scoped>
$spin_color: #ce0e2d;
$bg_color: #fff;
$color1: $spin_color;
$color2: #971317;
$color3: #d1d1d1;
$fontColor: #971317;

#bar-loading-wrapper {
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;

  background-color: #000;
  z-index: 998;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  .loading-prog {
    position: relative;
    overflow: hidden;
    width: 450px;
    max-width: 90vw;
    display: flex;
    justify-content: center;
    align-items: center;
    #loading-filter {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      opacity: 0.4;
      background: #000;
      opacity: 0.95;
      border-radius: 4px;
    }

    img {
      height: auto;
      width: 450px;
      max-width: 90vw;
      position: relative;
      // left: 2%;
    }
  }
  #loading-bar {
    position: relative;
    height: 2px;
    width: 450px;
    max-width: 90vw;
    padding: 0;
    border-radius: 24px;
    margin-top: 50px;
    overflow: hidden;
    #loading-bar-prog {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      opacity: 1;
      background: #fff;
      border-radius: 24px;
    }
    #loading-bar-antiprog {
      background: #fff;
      width: 100%;
      height: 100%;
      padding: 0;
      border-radius: 24px;
      opacity: 0.15;
    }
  }
}

#spinner-loading-wrapper {
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background-color: $bg_color;
  z-index: 998;
  .profile-main-loader {
    left: 50% !important;
    margin-left: -100px;
    position: fixed !important;
    top: 50% !important;
    margin-top: -100px;
    width: 45px;
    z-index: 998 !important;

    .loader {
      position: relative;
      margin: 0px auto;
      width: 200px;
      height: 200px;
    }
    .loader:before {
      content: "";
      display: block;
      padding-top: 100%;
    }

    .circular-loader {
      -webkit-animation: rotate 2s linear infinite;
      animation: rotate 2s linear infinite;
      height: 100%;
      -webkit-transform-origin: center center;
      -ms-transform-origin: center center;
      transform-origin: center center;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
      margin: auto;
    }

    .loader-path {
      stroke-dasharray: 150, 200;
      stroke-dashoffset: -10;
      -webkit-animation: dash 1.5s ease-in-out infinite,
        color 6s ease-in-out infinite;
      animation: dash 1.5s ease-in-out infinite, color 6s ease-in-out infinite;
      stroke-linecap: round;
    }

    @-webkit-keyframes rotate {
      100% {
        -webkit-transform: rotate(360deg);
        transform: rotate(360deg);
      }
    }

    @keyframes rotate {
      100% {
        -webkit-transform: rotate(360deg);
        transform: rotate(360deg);
      }
    }
    @-webkit-keyframes dash {
      0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
      }
      50% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -35;
      }
      100% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -124;
      }
    }
    @keyframes dash {
      0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
      }
      50% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -35;
      }
      100% {
        stroke-dasharray: 89, 200;
        stroke-dashoffset: -124;
      }
    }
    @-webkit-keyframes color {
      0% {
        stroke: $spin_color;
      }
      40% {
        stroke: $spin_color;
      }
      66% {
        stroke: $spin_color;
      }
      80%,
      90% {
        stroke: $spin_color;
      }
    }
    @keyframes color {
      0% {
        stroke: $spin_color;
      }
      40% {
        stroke: $spin_color;
      }
      66% {
        stroke: $spin_color;
      }
      80%,
      90% {
        stroke: $spin_color;
      }
    }
  }
}
</style>